<template>
  <LayoutCard
    v-if="tipsEnabled"
    data-cy="tip-personnel"
    :title="$t('components.tipPersonnel.title')"
    rounded
  >
    <div v-if="!orderPersonnel" class="tp-text-body grey--text">
      {{ subtitle }}
    </div>
    <WaiterCarousel v-if="!collective" @on-change="changeWaiter" />
    <v-expand-transition>
      <v-row v-if="showCta" no-gutters>
        <h4 v-if="!collective" class="pt-4">
          {{ $t('components.tipPersonnel.pickAmountTitle') }}
        </h4>
        <v-col>
          <div v-if="orderPersonnel" class="tp-text-body grey--text">
            <div class="d-flex flex-row align-center">
              <v-avatar size="64">
                <img
                  :src="getPhotoUrl(orderPersonnel)"
                  :alt="orderPersonnel.name"
                />
              </v-avatar>
              <div class="ml-3">
                <span class="tp-text-body-2">{{ orderPersonnel.name }}</span>
                <div class="tp-text-body grey--text">
                  {{ $t('components.tipPersonnel.personnelSubtitle') }}
                </div>
              </div>
            </div>
          </div>
          <TipsPicker
            :tips="tipChips"
            :min="minTipAmount"
            :max="maxTipAmount"
            @on-change="onTipsChange"
          />
          <div v-if="disclaimer" class="tp-text-body mt-3 grey--text">
            {{ disclaimer }}
          </div>
        </v-col>
      </v-row>
    </v-expand-transition>
  </LayoutCard>
</template>

<script>
import { debounce } from 'lodash';
import LayoutCard from '@/components/LayoutCard.vue';
import WaiterCarousel from '@/components/Tips/WaiterCarousel.vue';
import TipsPicker from '@/components/Cart/TipsPicker.vue';
import { tipChipsByPercentage } from '@/utils/tips';
import { defaultProfilePic } from '../../store/modules/user/api';

export default {
  components: {
    LayoutCard,
    WaiterCarousel,
    TipsPicker,
  },
  props: {
    amount: {
      type: Number,
      default: 0,
    },
    uniqueOrderNumber: {
      type: String,
      default: null,
    },
    orderPersonnel: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    selectedPersonnelId: null,
    tipAmount: null,
  }),
  computed: {
    personnelList() {
      return this.$store.getters.personnelList;
    },
    collective() {
      return this.$store.getters.functionalityCollectiveTipsEnabled;
    },
    tipsEnabled() {
      const hasPersonnel = this.personnelList.length > 0;
      const tipsEnabled = this.$store.getters.functionalityTipsEnabled;

      return (
        ((hasPersonnel && !this.collective) || this.collective) &&
        tipsEnabled &&
        this.amount > 0
      );
    },
    showCta() {
      if (this.collective) {
        return true;
      }

      return this.selectedPersonnelId !== null;
    },
    order() {
      return this.$store.getters.getPOSOrderByUniqueOrderNumber(
        this.uniqueOrderNumber
      );
    },
    guestCount() {
      return this.order?.guest_count || 1;
    },
    guestCountThreshold() {
      return this.$store.getters
        .customizationCollectiveTipsRequiredGuestCountThreshold;
    },
    requiredTipsConfig() {
      const guestCountThreshold = this.guestCountThreshold;

      if (!guestCountThreshold) {
        return undefined;
      }

      return {
        guestCountThreshold: guestCountThreshold,
        preselectedTipPercentage: 0.1,
      };
    },
    guestThresholdReached() {
      return this.guestCount >= this.guestCountThreshold;
    },
    minTipAmount() {
      if (this.guestThresholdReached) {
        return parseFloat(this.tipChips[0].value);
      }

      return this.$store.getters.minTipAmount;
    },
    maxTipAmount() {
      return this.$store.getters.maxTipAmount;
    },
    tipChips() {
      const formatPrice = amount =>
        this.$store.getters.getFormattedPrice(amount, 0);
      const customTipPercentages = this.$store.getters.customTipPercentages;
      const preselectedIndex = this.$store.getters
        .customizationCollectiveTipsDefaultSelectionIndex;

      const tipChips = tipChipsByPercentage({
        formatPrice,
        amount: this.amount,
        guestCount: this.guestCount,
        requiredTipsConfig: this.requiredTipsConfig,
        customTipPercentages,
        preselectedIndex,
      });

      return tipChips;
    },
    subtitle() {
      return this.collective
        ? this.$t('components.tipPersonnel.collective.subtitle')
        : this.$t('components.tipPersonnel.subtitle');
    },
    disclaimer() {
      const guestCountThreshold = this.guestCountThreshold;

      if (!guestCountThreshold || this.amount <= 1.8) {
        return undefined;
      }

      return this.$t('components.tipPersonnel.minTipAmountDisclaimer', {
        guestCount: guestCountThreshold,
      });
    },
  },
  watch: {
    selectedPersonnelId() {
      this.$emit('on-change', {
        personnelId: this.selectedPersonnelId,
        tipAmount: this.tipAmount,
      });
    },
    tipAmount: {
      handler: debounce(function(amount) {
        this.$emit('on-change', {
          personnelId: this.selectedPersonnelId,
          tipAmount: amount,
        });
      }, 300),
      immediate: true,
    },
  },
  async mounted() {
    // NOTE: not sure if this is the best place to fetch personnel
    await this.$store.dispatch('fetchPersonnel');
  },
  methods: {
    changeWaiter(selectedPersonnelId) {
      this.selectedPersonnelId = selectedPersonnelId;
    },
    onTipsChange(amount) {
      this.tipAmount = amount;
    },
    getPhotoUrl(personnel) {
      return personnel.photo || defaultProfilePic;
    },
  },
};
</script>

<style lang="scss" scoped></style>
